import React, { useState, useEffect, useRef } from "react";
import "./TreeView.scss";
import Dropdown from "./dropDown";
import { useUserData } from "../../hooks/useUserData";

function formatPlaceholderDueDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function truncatePlaceholderText(text, maxLen = 52) {
  const s = String(text || "").trim();
  if (!s) return "";
  return s.length <= maxLen ? s : `${s.slice(0, maxLen - 1)}…`;
}

function getPlaceholderStatusClass(status) {
  const s = String(status || "Pending");
  if (s === "Awaiting dates") return "tree-ph-status--awaiting";
  if (s === "Overdue") return "tree-ph-status--overdue";
  if (s === "Pending") return "tree-ph-status--pending";
  return "tree-ph-status--default";
}

function isStudyMilestoneLinked(node) {
  const code = String(
    node?.milestoneCode ?? node?.placeholderMeta?.MilestoneCode ?? "",
  ).trim();
  return code.length > 0 || Boolean(node?.milestoneId);
}

function nodeRequiresSignature(node) {
  if (!node || typeof node !== "object") return false;
  const raw =
    node.RequiresSignature ??
    node.requiresSignature ??
    node.placeholderMeta?.RequiresSignature ??
    node.milestoneMeta?.RequiresSignature;
  if (raw === true || raw === 1 || raw === "1") return true;
  const signerRoles = String(
    node?.SignerRoles ??
      node?.signerRoles ??
      node?.placeholderMeta?.SignerRoles ??
      node?.milestoneMeta?.DefaultSignerRoles ??
      "",
  ).trim();
  return signerRoles.length > 0;
}

function formatStudyAnchorRule(node) {
  const directRule = String(
    node?.dueAnchorRule ?? node?.placeholderMeta?.DueAnchorRule ?? "",
  ).trim();
  if (directRule) return directRule;

  const anchorType = String(
    node?.dueAnchorType ?? node?.placeholderMeta?.DueAnchorType ?? "",
  ).trim();
  const anchorCode = String(
    node?.dueAnchorMilestoneCode ??
      node?.placeholderMeta?.DueAnchorMilestoneCode ??
      "",
  ).trim();
  const offsetRaw =
    node?.dueOffsetDays ?? node?.placeholderMeta?.DueOffsetDays;
  const offset =
    offsetRaw === "" || offsetRaw == null ? null : Number(offsetRaw);

  if (anchorType === "AnotherMilestone" && anchorCode) {
    if (offset != null && !Number.isNaN(offset) && offset !== 0) {
      return offset > 0 ? `${anchorCode} +${offset}d` : `${anchorCode} ${offset}d`;
    }
    return anchorCode;
  }
  if (anchorType === "ReferenceDate") {
    if (offset != null && !Number.isNaN(offset) && offset !== 0) {
      return offset > 0
        ? `Reference date +${offset}d`
        : `Reference date ${offset}d`;
    }
    return "Reference date";
  }
  if (offset != null && !Number.isNaN(offset) && offset !== 0) {
    return offset > 0 ? `+${offset}d` : `${offset}d`;
  }
  return "";
}

function computeEffectivePlaceholderDueDate(
  node,
  siteReferenceDate,
  milestoneAnchorDates = {},
) {
  const existing = node?.dueDate || node?.placeholderMeta?.DueDate;
  if (existing) return existing;

  const anchorType = String(
    node?.dueAnchorType ?? node?.placeholderMeta?.DueAnchorType ?? "",
  ).trim();
  const offsetRaw =
    node?.dueOffsetDays ?? node?.placeholderMeta?.DueOffsetDays;
  const offset =
    offsetRaw === "" || offsetRaw == null ? 0 : Number(offsetRaw);
  const anchorCode = String(
    node?.dueAnchorMilestoneCode ??
      node?.placeholderMeta?.DueAnchorMilestoneCode ??
      "",
  ).trim();

  let baseValue = "";
  if (anchorType === "ReferenceDate") {
    baseValue = siteReferenceDate || "";
  } else if (anchorType === "AnotherMilestone" && anchorCode) {
    baseValue = milestoneAnchorDates[anchorCode] || "";
  } else {
    return null;
  }

  if (!baseValue) return null;
  const base = new Date(baseValue);
  if (Number.isNaN(base.getTime())) return null;
  base.setDate(base.getDate() + (Number.isNaN(offset) ? 0 : offset));
  return base.toISOString();
}

function studyPlaceholderAwaitingDates(
  node,
  siteReferenceDate,
  milestoneAnchorDates = {},
) {
  if (node?.isTemplatePlaceholder) return false;
  if (!isStudyMilestoneLinked(node)) return false;
  return !computeEffectivePlaceholderDueDate(
    node,
    siteReferenceDate,
    milestoneAnchorDates,
  );
}

function buildPlaceholderTooltip(
  node,
  siteReferenceDate,
  milestoneAnchorDates = {},
) {
  const parts = [node.name];
  const desc = node.placeholderMeta?.Description ?? node.description;
  if (desc) parts.push(desc);
  parts.push(node.isRequired === false ? "Optional" : "Required");
  if (node.isTemplatePlaceholder) {
    const days = node.placeholderMeta?.DefaultDueDays;
    if (days != null && days !== "") parts.push(`Due day ${days}`);
  } else {
    const anchorRule = formatStudyAnchorRule(node);
    if (anchorRule) parts.push(`Timing: ${anchorRule}`);
    const due = formatPlaceholderDueDate(
      computeEffectivePlaceholderDueDate(
        node,
        siteReferenceDate,
        milestoneAnchorDates,
      ),
    );
    if (due) parts.push(`Due ${due}`);
    const assignee = (node.assignedToName || "").trim();
    if (assignee) parts.push(`Assigned to ${assignee}`);
    if (nodeRequiresSignature(node)) parts.push("Signature required");
    if (
      studyPlaceholderAwaitingDates(
        node,
        siteReferenceDate,
        milestoneAnchorDates,
      )
    ) {
      parts.push("Awaiting milestone dates");
    } else if (node.placeholderStatus) {
      parts.push(`Status: ${node.placeholderStatus}`);
    }
  }
  return parts.join(" · ");
}

function PlaceholderTreeLabel({
  node,
  siteReferenceDate = "",
  milestoneAnchorDates = {},
}) {
  const isTemplate = node.isTemplatePlaceholder === true;
  const isAdHoc = node.isAdHoc === true || node.IsAdHoc === true || node.IsAdHoc === 1;
  const status = node.placeholderStatus || "Pending";
  const awaitingDates = studyPlaceholderAwaitingDates(
    node,
    siteReferenceDate,
    milestoneAnchorDates,
  );
  const displayStatus = awaitingDates ? "Awaiting dates" : status;
  const dueRaw = computeEffectivePlaceholderDueDate(
    node,
    siteReferenceDate,
    milestoneAnchorDates,
  );
  const anchorRule = !isTemplate ? formatStudyAnchorRule(node) : "";
  const dueLabel = isTemplate
    ? node.placeholderMeta?.DefaultDueDays != null &&
      node.placeholderMeta?.DefaultDueDays !== ""
      ? `Day ${node.placeholderMeta.DefaultDueDays}`
      : ""
    : dueRaw
      ? formatPlaceholderDueDate(dueRaw)
      : "";
  const desc = truncatePlaceholderText(
    node.placeholderMeta?.Description ?? node.description,
  );
  const assignee = !isTemplate ? (node.assignedToName || "").trim() : "";
  const isOverdue = !isTemplate && !awaitingDates && status === "Overdue";
  const showDueChip = Boolean(dueLabel || anchorRule);
  const showMetaRow =
    showDueChip ||
    !isTemplate ||
    (isTemplate &&
      node.placeholderMeta?.DefaultDueDays != null &&
      node.placeholderMeta?.DefaultDueDays !== "");
  const showDetailRow = Boolean(assignee || desc);

  return (
    <span
      className={`tree-ph-slot ${
        isTemplate ? "tree-ph-slot--template" : "tree-ph-slot--study"
      }${isOverdue ? " tree-ph-slot--overdue" : ""}`}
    >
      <span className="tree-ph-slot__main">
        <i
          className={`tree-ph-slot__icon ${
            isTemplate
              ? "far fa-file-lines"
              : isOverdue
                ? "fas fa-file-circle-exclamation"
                : "fas fa-clipboard-list"
          }`}
          aria-hidden="true"
        />
        <span className="tree-ph-slot__name">{node.name}</span>
        {nodeRequiresSignature(node) ? (
          <i
            className="fas fa-file-signature tree-ph-sig-icon"
            title="Signature required"
            aria-label="Signature required"
          />
        ) : null}
        {isTemplate ? (
          <span
            className={`tree-ph-req ${
              node.isRequired === false
                ? "tree-ph-req--optional"
                : "tree-ph-req--required"
            }`}
          >
            {node.isRequired === false ? "Optional" : "Required"}
          </span>
        ) : (
          <span
            className={`tree-ph-status ${getPlaceholderStatusClass(displayStatus)}`}
          >
            {displayStatus}
          </span>
        )}
      </span>
      {showMetaRow && (
        <span className="tree-ph-slot__meta">
          {!isTemplate && node.milestoneCode && !node.underMilestoneGroup && !isAdHoc && (
            <span className="tree-ph-meta-chip tree-ph-meta-chip--milestone">
              {node.milestoneCode}
            </span>
          )}
          {!isTemplate && (
            <span
              className={`tree-ph-meta-chip ${
                node.isRequired === false
                  ? "tree-ph-meta-chip--optional"
                  : "tree-ph-meta-chip--required"
              }`}
            >
              {node.isRequired === false ? "Optional" : "Required"}
            </span>
          )}
          {showDueChip && (
            <span
              className={`tree-ph-meta-chip ${
                anchorRule && !dueLabel
                  ? "tree-ph-meta-chip--anchor"
                  : "tree-ph-meta-chip--due"
              }${isOverdue ? " tree-ph-meta-chip--overdue" : ""}`}
            >
              <i
                className={`far ${anchorRule && !dueLabel ? "fa-link" : "fa-calendar"}`}
                aria-hidden="true"
              />
              {isTemplate
                ? `Due day ${node.placeholderMeta.DefaultDueDays}`
                : dueLabel
                  ? `Due ${dueLabel}`
                  : anchorRule}
            </span>
          )}
        </span>
      )}
      {showDetailRow && (
        <span className="tree-ph-slot__meta tree-ph-slot__meta--detail">
          {assignee && (
            <span className="tree-ph-meta-chip tree-ph-meta-chip--assignee">
              <i className="far fa-user" aria-hidden="true" />
              {assignee}
            </span>
          )}
          {desc && (
            <span
              className="tree-ph-meta-chip tree-ph-meta-chip--desc"
              title={node.placeholderMeta?.Description ?? node.description}
            >
              {desc}
            </span>
          )}
        </span>
      )}
    </span>
  );
}

const getFileIcon = (filename) => {
  const safeName = String(filename ?? "").trim();
  if (!safeName || !safeName.includes(".")) return "fas fa-file";
  const ext = safeName.split(".").pop().toLowerCase();
  switch (ext) {
    case "pdf":
      return "fas fa-file-pdf text-danger";
    case "doc":
    case "docx":
      return "fas fa-file-word text-primary";
    case "xls":
    case "xlsx":
      return "fas fa-file-excel text-success";
    case "ppt":
    case "pptx":
      return "fas fa-file-powerpoint text-warning";
    case "txt":
      return "fas fa-file-alt text-secondary";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
      return "fas fa-file-image text-info";
    default:
      return "fas fa-file";
  }
};

const nodeDisplayName = (node) => String(node?.name ?? "").trim();

// Enhanced nodeMatchesSearch with Version 2 logic
const nodeMatchesSearch = (node, term, searchType) => {
  if (!term) return true;
  const lower = term.toLowerCase();
  const nodeName = nodeDisplayName(node).toLowerCase();

  if (searchType === "folder") {
    if (node.type === "folder" && nodeName.includes(lower))
      return true;
    return node.children?.some((child) =>
      nodeMatchesSearch(child, term, searchType)
    );
  }

  if (searchType === "file") {
    if (node.type === "file" && nodeName.includes(lower))
      return true;
    return node.children?.some((child) =>
      nodeMatchesSearch(child, term, searchType)
    );
  }

  // For "both" search type
  if (nodeName.includes(lower)) return true;
  return node.children?.some((child) =>
    nodeMatchesSearch(child, term, searchType)
  );
};

// Advanced tree filtering function (Version 2 style) - Fixed for file search
const filterTreeData = (nodes, term, type) => {
  if (!term || term.length < 3) return nodes;

  const filterNode = (node) => {
    const nodeMatches = nodeDisplayName(node)
      .toLowerCase()
      .includes(term.toLowerCase());

    if (type === "folder") {
      // If this folder matches, return entire subtree
      if (nodeMatches && node.type === "folder") {
        return node; // Return complete node with all children
      }

      // If folder doesn't match but has matching descendant folders
      if (node.children) {
        const filteredChildren = node.children
          .map((child) => filterNode(child))
          .filter((child) => child !== null);

        if (filteredChildren.length > 0) {
          return {
            ...node,
            children: filteredChildren,
          };
        }
      }

      return null;
    }

    if (type === "file") {
      // For file search - only match files, not folders
      if (nodeMatches && node.type === "file") {
        return node;
      }

      // Check if any descendant files match
      if (node.children) {
        const filteredChildren = node.children
          .map((child) => filterNode(child))
          .filter((child) => child !== null);

        if (filteredChildren.length > 0) {
          return {
            ...node,
            children: filteredChildren,
          };
        }
      }

      return null;
    }

    // For "both" search type
    const hasMatchingChildren = node.children?.some((child) =>
      nodeMatchesSearch(child, term, type)
    );

    if (!nodeMatches && !hasMatchingChildren) {
      return null;
    }

    if (node.children) {
      const filteredChildren = node.children
        .map((child) => filterNode(child))
        .filter((child) => child !== null);

      return {
        ...node,
        children: filteredChildren,
      };
    }

    return nodeMatches ? node : null;
  };

  return nodes.map((node) => filterNode(node)).filter((node) => node !== null);
};

const TreeNode = ({
  node,
  onAction,
  onSelect,
  selectedNode,
  setSelectedNode,
  search,
  searchType,
  expandedIds,
  toggleNodeExpand,
  draggedNode,
  dragOverNode,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  canMove = true, // Permission check prop
  nodeRefs, // NEW: Refs for scrolling
  treeToolBar,
  canShowGenerateCertification = () => false,
  canShowSignMenu,
  canShowCreateCapa = () => false,
  folderMenuVariant = "default",
  showFolderNewFile = true,
  canEditPlaceholders = false,
  siteReferenceDate = "",
  milestoneAnchorDates = {},
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();
  const nodeRef = useRef(); // NEW: Ref for this node
  const nodeIdKey = (id) =>
    id == null || id === "" ? "" : String(id).trim().toLowerCase();
  const isExpanded = [...expandedIds].some(
    (id) => nodeIdKey(id) === nodeIdKey(node.id),
  );
  const isSelected = selectedNode?.id === node.id;
  const isDragOver = dragOverNode?.id === node.id;
  const isDragging = draggedNode?.id === node.id;
  const { userData } = useUserData();
  // NEW: Store ref for this node
  useEffect(() => {
    if (nodeRef.current && nodeRefs) {
      nodeRefs.current[node.id] = nodeRef.current;
    }
  }, [node.id, nodeRefs]);

  // NEW: Scroll into view when selected
  useEffect(() => {
    if (isSelected && nodeRef.current) {
      const el = nodeRef.current;
      const id = setTimeout(() => {
        if (el?.isConnected && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
      return () => clearTimeout(id);
    }
  }, [isSelected]);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectNode = () => {
    if (node.type === "folder") {
      setSelectedNode(node);
      // Always notify parent so consumers (e.g. ISF) can clear document preview / details
      onSelect(node, "Selection");
      return;
    }
    if (node !== selectedNode) {
      setSelectedNode(node);
      onSelect(node, "Selection");
    }
  };

  const handleToggleClick = (e) => {
    e.stopPropagation();
    selectNode();
    toggleNodeExpand(node.id);
  };

  const handleLabelClick = () => {
    selectNode();
  };

  const handleMenuIconClick = (e) => {
    e.stopPropagation();
    selectNode();
    setMenuOpen((prev) => !prev);
  };

  // Drag and Drop handlers
  const handleDragStart = (e) => {
    if (!canMove) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    onDragStart(node);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    if (!canMove) return;

    e.preventDefault();
    e.stopPropagation();

    // Only allow drop on folders
    if (node.type !== "folder") {
      return;
    }

    // Don't allow dropping on self
    if (draggedNode && draggedNode.id === node.id) {
      return;
    }

    // Don't allow dropping a folder into its own descendants
    if (draggedNode && draggedNode.type === "folder") {
      if (isDescendant(node, draggedNode)) {
        return;
      }
    }

    e.dataTransfer.dropEffect = "move";
    onDragOver(node);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragOver(null);
  };

  const handleDrop = (e) => {
    if (!canMove) return;

    e.preventDefault();
    e.stopPropagation();

    // Only allow drop on folders
    if (node.type !== "folder") {
      return;
    }

    // Don't allow dropping on self
    if (draggedNode && draggedNode.id === node.id) {
      return;
    }

    // Don't allow dropping a folder into its own descendants
    if (draggedNode && draggedNode.type === "folder") {
      if (isDescendant(node, draggedNode)) {
        return;
      }
    }

    onDrop(node);
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragEnd();
  };

  // Check if targetNode is a descendant of sourceNode
  const isDescendant = (targetNode, sourceNode) => {
    if (!sourceNode.children) return false;

    for (let child of sourceNode.children) {
      if (child.id === targetNode.id) return true;
      if (isDescendant(targetNode, child)) return true;
    }
    return false;
  };

  // visibleChildren logic - simplified for Version 2 behavior
  const visibleChildren = node.children || [];
  const hasChildren = visibleChildren.length > 0;
  const isExpandableNode =
    (node.type === "folder" ||
      node.type === "milestone" ||
      (node.type === "placeholder" && hasChildren) ||
      (node.type === "file" && hasChildren)) &&
    hasChildren;

  const showActionMenu =
    !node.isPreviousVersion &&
    (node.type === "file" ||
      (node.type === "folder" && folderMenuVariant === "template") ||
      (node.type === "folder" && folderMenuVariant !== "template"));

  const isPlaceholderSlot = node.type === "placeholder";

  // Determine if this node can be a valid drop target
  const isValidDropTarget =
    canMove &&
    node.type === "folder" &&
    draggedNode &&
    draggedNode.id !== node.id &&
    (draggedNode.type === "file" ||
      (draggedNode.type === "folder" && !isDescendant(node, draggedNode)));

  return (
    <div className="tree-node" ref={nodeRef}>
      <div
        className={`node-content d-flex ${
          isPlaceholderSlot ? "align-items-start" : "align-items-center"
        } ${isSelected ? "selected" : ""} ${
          isDragOver && isValidDropTarget ? "drag-over" : ""
        } ${isDragging ? "dragging" : ""}${
          isPlaceholderSlot ? " is-placeholder-slot" : ""
        }${
          isPlaceholderSlot && node.isTemplatePlaceholder
            ? " is-template-placeholder-slot"
            : ""
        }${
          isPlaceholderSlot && !node.isTemplatePlaceholder
            ? " is-study-placeholder-slot"
            : ""
        }${node.isPreviousVersion ? " is-previous-version" : ""}`}
        data-type={node.type}
        draggable={
          canMove && node.type !== "placeholder" && !node.isPreviousVersion
        }
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
      >
        {isExpandableNode && (
          <span className="toggle-icon" onClick={handleToggleClick}>
            {isExpanded ? "−" : "+"}
          </span>
        )}

        <span
          className={`node-label node-type-${node.type}${
            isPlaceholderSlot ? " is-placeholder-label" : ""
          }${
            isPlaceholderSlot && node.isTemplatePlaceholder
              ? " is-template-placeholder"
              : ""
          }${
            isPlaceholderSlot && !node.isTemplatePlaceholder
              ? " is-study-placeholder"
              : ""
          }`}
          title={
            isPlaceholderSlot
              ? buildPlaceholderTooltip(
                  node,
                  siteReferenceDate,
                  milestoneAnchorDates,
                )
              : node.name
          }
          onClick={handleLabelClick}
        >
          {node.type === "folder" && (
            <>
              <i
                className={`fa ${
                  isExpanded ? "fa-folder-open" : "fa-folder"
                } me-2 text-primary`}
              />
              {node.isRestricted === true && (
                <i
                  className="fas fa-registered me-2 text-danger"
                  title="Resricted"
                ></i>
              )}
            </>
          )}

          {node.type === "file" &&
            (node.isPreviousVersion ? (
              <i
                className="fas fa-history me-2 text-muted"
                title="Older version (read-only)"
              />
            ) : node.isLocked ? (
              <i className="fas fa-lock me-2 text-danger" />
            ) : (
              <i className={`${getFileIcon(node.name || "")} me-2`} />
            ))}

          {node.type === "placeholder" && (
            <PlaceholderTreeLabel
              node={node}
              siteReferenceDate={siteReferenceDate}
              milestoneAnchorDates={milestoneAnchorDates}
            />
          )}

          {node.type === "milestone" && (
            <>
              <i className="fas fa-flag me-2 flex-shrink-0 text-warning" />
              <span className="tree-node-text">{node.name}</span>
              {nodeRequiresSignature(node) ? (
                <i
                  className="fas fa-file-signature tree-node-sig-icon ms-1"
                  title="Signature required"
                  aria-label="Signature required"
                />
              ) : null}
              {node.isManualMilestone && canEditPlaceholders ? (
                <button
                  type="button"
                  className="tree-node-milestone-remove"
                  title="Remove from folder"
                  aria-label={`Remove ${node.name || "milestone"} from folder`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction(node, "removeAdHocMilestone");
                  }}
                >
                  <i className="fas fa-trash-alt" aria-hidden="true" />
                </button>
              ) : null}
            </>
          )}

          {node.type !== "placeholder" && node.type !== "milestone" && node.name}
        </span>

        <div
          className="action-menu-wrapper position-relative ms-1"
          ref={menuRef}
        >
          {treeToolBar && showActionMenu && (
            <i
              className="fa fa-caret-down action-menu-icon"
              onClick={handleMenuIconClick}
            ></i>
          )}

          {menuOpen && (
            <div className="action-menu shadow-sm">
              {node.type === "file" && (
                <>
                  <div onClick={() => onAction(node, "replaceFile")}>
                    Upload new version
                  </div>
                  <div
                    onClick={() =>
                      onAction(node, node.isLocked ? "unlockFile" : "lockFile")
                    }
                  >
                    {node.isLocked ? "Unlock" : "Lock"}
                  </div>
                  {(canShowSignMenu
                    ? canShowSignMenu(node)
                    : node.isMandatorySign) && (
                      <div onClick={() => onAction(node, "signFile")}>
                        Sign
                      </div>
                    )}
                  {canShowGenerateCertification(node) && (
                    <div
                      onClick={() =>
                        onAction(node, "generateCertification")
                      }
                    >
                      Generate certification
                    </div>
                  )}
                  {node.CertificationId && (
                    <div
                      onClick={() =>
                        onAction(node, "viewCertification")
                      }
                    >
                      View certification
                    </div>
                  )}
                  {canShowCreateCapa(node) && (
                    <div onClick={() => onAction(node, "createCapa")}>
                      Create CAPA
                    </div>
                  )}
                  <div onClick={() => onAction(node, "auditLog")}>
                    Audit Log
                  </div>
                </>
              )}

              {node.type === "folder" && folderMenuVariant === "template" && (
                <>
                  <div onClick={() => onAction(node, "subFolder")}>
                    Add sub folder
                  </div>
                  <div onClick={() => onAction(node, "renameFolder")}>
                    Rename
                  </div>
                  <div onClick={() => onAction(node, "deleteFolder")}>
                    Delete
                  </div>
                </>
              )}

              {node.type === "folder" && folderMenuVariant !== "template" && (
                <>
                  {showFolderNewFile && (
                    <div onClick={() => onAction(node, "newFile")}>New File</div>
                  )}
                  <div onClick={() => onAction(node, "renameFolder")}>
                    Rename
                  </div>
                  <div onClick={() => onAction(node, "subFolder")}>
                    Add Sub Folder
                  </div>
                  <div onClick={() => onAction(node, "deleteFolder")}>
                    Delete
                  </div>
                  {userData?.Role === "System Administrator" && (
                    <div onClick={() => onAction(node, "restrictedFolder")}>
                      Restrict Foder
                    </div>
                  )}
                  <div onClick={() => onAction(node, "auditLog")}>
                    Audit Log
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {node.children && isExpanded && hasChildren && (
        <div className="tree-children">
          {visibleChildren.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              onAction={onAction}
              onSelect={onSelect}
              selectedNode={selectedNode}
              setSelectedNode={setSelectedNode}
              search={search}
              searchType={searchType}
              expandedIds={expandedIds}
              toggleNodeExpand={toggleNodeExpand}
              draggedNode={draggedNode}
              dragOverNode={dragOverNode}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
              canMove={canMove}
              nodeRefs={nodeRefs}
              treeToolBar={treeToolBar}
              canShowGenerateCertification={canShowGenerateCertification}
              canShowSignMenu={canShowSignMenu}
              canShowCreateCapa={canShowCreateCapa}
              folderMenuVariant={folderMenuVariant}
              showFolderNewFile={showFolderNewFile}
              canEditPlaceholders={canEditPlaceholders}
              siteReferenceDate={siteReferenceDate}
              milestoneAnchorDates={milestoneAnchorDates}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TreeView = ({
  data = [],
  onAction = () => {},
  onSelect = () => {},
  canMove = true, // Permission prop - can be controlled by parent
  selectedNodeId = null, // NEW: Node ID to select programmatically
  treeToolBar = true,
  canShowGenerateCertification = () => false,
  canShowSignMenu,
  canShowCreateCapa = () => false,
  /** 'template' = ISF template editor: add sub folder, rename, delete (root folder from toolbar) */
  folderMenuVariant = "default",
  canEditPlaceholders = false,
  forceExpandedIds = [],
  showFolderNewFile = true,
  siteReferenceDate = "",
  milestoneAnchorDates = {},
}) => {
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState("file");
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOverNode, setDragOverNode] = useState(null);
  const nodeRefs = useRef({}); // NEW: Store refs for all nodes

  const idsMatch = (a, b) => {
    if (a == null || b == null || a === "" || b === "") return false;
    return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
  };

  // NEW: Effect to handle programmatic selection
  useEffect(() => {
    if (selectedNodeId == null || selectedNodeId === "") return;
    const node = findNodeById(data, selectedNodeId);
    if (node) {
      setSelectedNode((prev) => (idsMatch(prev?.id, node.id) ? prev : node));
      expandPathToNode(data, selectedNodeId);
    }
  }, [selectedNodeId, data]);

  useEffect(() => {
    if (!forceExpandedIds?.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      forceExpandedIds.forEach((id) => next.add(id));
      return next;
    });
  }, [forceExpandedIds, data]);

  // NEW: Find node by ID recursively
  const findNodeById = (nodes, id) => {
    for (let node of nodes) {
      if (idsMatch(node.id, id)) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // NEW: Expand all parent folders to show the selected node
  const expandPathToNode = (nodes, targetId, path = []) => {
    for (let node of nodes) {
      const currentPath = [...path, node.id];

      if (idsMatch(node.id, targetId)) {
        // Found the target, expand all nodes in path
        const newExpanded = new Set(expandedIds);
        path.forEach((id) => newExpanded.add(id));
        setExpandedIds(newExpanded);
        return true;
      }

      if (node.children) {
        const found = expandPathToNode(node.children, targetId, currentPath);
        if (found) return true;
      }
    }
    return false;
  };

  const handleSelect = (node) => {
    setSelectedNode(node);
    onSelect(node, "Selection");
  };

  const collectIds = (nodes, ids) => {
    nodes.forEach((n) => {
      ids.add(n.id);
      if (n.children) collectIds(n.children, ids);
    });
  };

  const toggleAll = () => {
    setAllExpanded((prev) => {
      const newVal = !prev;
      if (newVal) {
        const ids = new Set();
        collectIds(data, ids);
        setExpandedIds(ids);
      } else {
        setExpandedIds(new Set());
      }
      return newVal;
    });
  };

  const toggleNodeExpand = (id) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const expandToMatchingNodes = (nodes, term, type, path = []) => {
    let matchedPaths = [];
    for (let n of nodes) {
      const matches = nodeMatchesSearch(n, term, type);
      if (matches) matchedPaths.push([...path, n.id]);
      if (n.children) {
        matchedPaths = matchedPaths.concat(
          expandToMatchingNodes(n.children, term, type, [...path, n.id])
        );
      }
    }
    return matchedPaths;
  };

  const handleSearchChange = (val) => {
    setSearch(val);

    if (!val || val.length < 3) {
      setExpandedIds(new Set());
      setAllExpanded(false);
      return;
    }

    const paths = expandToMatchingNodes(data, val, searchType);
    const ids = new Set();
    paths.forEach((p) => p.forEach((id) => ids.add(id)));
    setExpandedIds(ids);
  };

  // Drag and Drop handlers
  const handleDragStart = (node) => {
    if (!canMove) return;
    setDraggedNode(node);
  };

  const handleDragOver = (node) => {
    if (!canMove) return;
    setDragOverNode(node);
  };

  const handleDrop = (targetNode) => {
    if (!canMove || !draggedNode || !targetNode) {
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    // Validation: Only allow drop on folders
    if (targetNode.type !== "folder") {
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    // Don't allow dropping on self
    if (draggedNode.id === targetNode.id) {
      setDraggedNode(null);
      setDragOverNode(null);
      return;
    }

    // Check if target is a descendant of source (for folders)
    if (draggedNode.type === "folder") {
      const isDescendant = checkIsDescendant(targetNode, draggedNode);
      if (isDescendant) {
        setDraggedNode(null);
        setDragOverNode(null);
        return;
      }
    }

    // Call onAction with source and destination nodes
    onAction(
      {
        sourceNode: draggedNode,
        destinationNode: targetNode,
      },
      "moveNode"
    );

    setDraggedNode(null);
    setDragOverNode(null);
  };

  const handleDragEnd = () => {
    setDraggedNode(null);
    setDragOverNode(null);
  };

  // Check if targetNode is a descendant of sourceNode
  const checkIsDescendant = (targetNode, sourceNode) => {
    if (!sourceNode.children) return false;

    for (let child of sourceNode.children) {
      if (child.id === targetNode.id) return true;
      if (checkIsDescendant(targetNode, child)) return true;
    }
    return false;
  };

  // Updated filteredData with advanced filtering
  const filteredData =
    !search || search.length < 3
      ? data
      : filterTreeData(data, search, searchType);

  // Dropdown options for search type

  return (
    <div className={`tree-view ${draggedNode ? "dragging-active" : ""}`}>
      {treeToolBar && (
        <div className="tree-toolbar mb-2">
          {folderMenuVariant === "template" ? (
            <div className="d-flex gap-2 align-items-center">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={toggleAll}
                title={allExpanded ? "Collapse All" : "Expand All"}
              >
                <i
                  className={`fa ${
                    allExpanded ? "fa-minus-square" : "fa-plus-square"
                  }`}
                />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => onAction(null, "newFolder")}
                title="Add root folder"
              >
                <i className="fa fa-folder-plus me-1" />
                Add root folder
              </button>
            </div>
          ) : (
            <>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={toggleAll}
                  title={allExpanded ? "Collapse All" : "Expand All"}
                  style={{ width: "25%" }}
                >
                  <i
                    className={`fa ${
                      allExpanded ? "fa-minus-square" : "fa-plus-square"
                    }`}
                  ></i>
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => onAction(null, "newFolder")}
                  title="Add Folder"
                  style={{ width: "25%" }}
                >
                  <i
                    className="fa fa-folder-plus"
                    style={{ fontSize: "14px" }}
                  ></i>
                </button>
                <select
                  style={{ width: "9.2rem" }}
                  className="form-sleect form-select-sm"
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                >
                  <option value="file">File</option>
                  <option value="folder">Folder</option>
                  {/* <option value="both">Both</option> */}
                </select>
              </div>
              <div className="mt-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search (min 3 chars)..."
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  style={{ maxWidth: "100%" }}
                  autoComplete="off"
                />
              </div>
            </>
          )}
        </div>
      )}

      {filteredData.length === 0 && search.length >= 3 ? (
        <div>No records found</div>
      ) : (
        filteredData.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            onAction={onAction}
            onSelect={handleSelect}
            selectedNode={selectedNode}
            setSelectedNode={setSelectedNode}
            search={search.length >= 3 ? search : ""}
            searchType={searchType}
            expandedIds={expandedIds}
            toggleNodeExpand={toggleNodeExpand}
            draggedNode={draggedNode}
            dragOverNode={dragOverNode}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            canMove={canMove}
            nodeRefs={nodeRefs}
            treeToolBar={treeToolBar}
            canShowGenerateCertification={canShowGenerateCertification}
            canShowSignMenu={canShowSignMenu}
            canShowCreateCapa={canShowCreateCapa}
            folderMenuVariant={folderMenuVariant}
            showFolderNewFile={showFolderNewFile}
            canEditPlaceholders={canEditPlaceholders}
            siteReferenceDate={siteReferenceDate}
            milestoneAnchorDates={milestoneAnchorDates}
          />
        ))
      )}
    </div>
  );
};

export default TreeView;
